// src/Admin/UserManagementPane.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Snackbar,
  Alert,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  IconButton,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Switch,
  Checkbox,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  Divider,
} from "@mui/material";
import {
  PersonAddAlt as PersonAddAltIcon,
  Send as SendIcon,
  LockReset as LockResetIcon,
  Delete as DeleteIcon,
  PauseCircle as PauseCircleIcon,
  PlayCircle as PlayCircleIcon,
  Refresh as RefreshIcon,
  Folder as FolderIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import { safeFetch } from "./Safefetch";


/* ------------------------------------------------------------------
   Simple HTTP helper using config.API_SERVER
------------------------------------------------------------------ */
//const BASE = ("http://127.0.0.1:8000")
const BASE = ("http://ec2-43-205-136-189.ap-south-1.compute.amazonaws.com")

const toQuery = (params = {}) =>
  Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join("&");

async function http(path, { method = "POST", data, params, headers } = {}) {
  const upper = (method || "POST").toUpperCase();
  const isGetLike = upper === "GET" || upper === "HEAD";

  // Get the LL token
  const token = localStorage.getItem("labour_law_token");

  // Build URL
  const url = isGetLike
    ? `${BASE}${path}${params ? `?${toQuery(params)}` : ""}`
    : `${BASE}${path}`;

  console.log("🌐 HTTP", upper, url, { params, data });

  // Make the request
  const res = await safeFetch(url, {
    method: upper,
    headers: {
      "Content-Type": "application/json",

      // ⬇️⬇️⬇️ ADD THIS LINE (the only change needed)
      ...(token ? { Authorization: `Bearer ${token}` } : {}),

      ...(headers || {}),
    },
    body: isGetLike ? undefined : JSON.stringify(data || {}),
    credentials: "include",
  });

  const ct = res.headers.get("content-type") || "";
  const isJSON = ct.includes("application/json");
  const body = isJSON ? await res.json() : await res.text();

  if (!res.ok) {
    console.error("🔴 HTTP ERROR", res.status, body);
    const msg = isJSON ? body?.error || body?.message : body;
    throw new Error(msg || `HTTP ${res.status}`);
  }

  return body;
}

/* ------------------------------------------------------------------
   Local AdminUsersAPI built on top of http() + config
------------------------------------------------------------------ */
const AdminUsersAPI = {
  // GET /usermgmt?q=&role=&status=&page=&pageSize=
  list({ q, role, status, page = 1, pageSize = 10 } = {}) {
    return http("/usermgmt/list_users", {
      method: "GET",
      params: { q, role, status, page, pageSize },
    });
  },

  // POST /usermgmt/create
  create(payload) {
    return http("/usermgmt/create", {
      method: "POST",
      data: payload,
    });
  },

  // POST /usermgmt/invite
  invite({ email }) {
    return http("/usermgmt/invite", {
      method: "POST",
      data: { email },
    });
  },

  // POST /usermgmt/update_role
  updateRole({ id, role }) {
    return http("/users/update_role", {
      method: "POST",
      data: { id, role },
    });
  },

  // POST /usermgmt/toggle_status
  toggleStatus({ id, status }) {
    return http("/usermgmt/toggle_status", {
      method: "POST",
      data: { id, status },
    });
  },

  // POST /usermgmt/reset_password
  resetPassword({ id }) {
    return http("/usermgmt/reset_password", {
      method: "POST",
      data: { id },
    });
  },

  // POST /usermgmt/resend_invite
  resendInvite({ id }) {
    return http("/usermgmt/resend_invite", {
      method: "POST",
      data: { id },
    });
  },

  // POST /usermgmt/delete
  deleteUser({ id }) {
    console.log("🧨 AdminUsersAPI.deleteUser -> /usermgmt/delete, id:", id);
    return http("/usermgmt/delete", {
      method: "POST",
      data: { id },
    });
  },

  // POST /usermgmt/update_can_upload
  updateCanUpload({ id, can_upload }) {
    return http("/usermgmt/update_can_upload", {
      method: "POST",
      data: { id, can_upload },
    });
  },

  // GET /usermgmt/list_uploads?userId=&days=
  listUserUploads({ userId, days = 30 }) {
    return http("/usermgmt/list_uploads", {
      method: "GET",
      params: { userId, days },
    });
  },
  updateUserValidity({ user_id, valid_from, valid_to }) {
    return http("/usermgmt/update_user_validity", {
      method: "POST",
      data: { user_id, valid_from, valid_to },
    });
  },
  updateUserLabourPackage({ user_id, product_id }) {
    return http("/usermgmt/update_user_labour_package", {
      method: "POST",
      data: { user_id, product_id },
    });
  },

  // POST /usermgmt/import_from_user_s3
  importFromUserS3(payload) {
    return http("/usermgmt/import_from_user_s3", {
      method: "POST",
      data: payload,
    });
  },
  listLabourPackages() {
    return http("/usermgmt/list_labour_packages", {
      method: "GET",
    });
  },
  createProduct(payload) {
    return http("/usermgmt/create_product", {
      method: "POST",
      data: payload,
    });
  },

  updateProduct(id, payload) {
    return http(`/usermgmt/update_product/${id}`, {
      method: "PUT",
      data: payload,
    });
  },
  deleteProduct(id, payload) {
    return http(`/usermgmt/delete_product/${id}`, {
      method: "PUT",
      data: payload,
    });
  },
};

/* ------------------------------------------------------------------
   Original component logic
------------------------------------------------------------------ */

const ROLE_OPTIONS = ["member", "guest"];
const STATUS_COLORS = { active: "success", invited: "warning", suspended: "default" };

const fmtDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString()}`;
};


/* ---------------------------
   S3 Review / Import Dialog
----------------------------*/
function UserUploadsDialog({ open, onClose, user, onImportDone }) {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [aiPromptOpen, setAiPromptOpen] = useState(false);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await AdminUsersAPI.listUserUploads({ userId: user.id });
      setItems(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      console.error("Failed to load user uploads", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setItems([]);
      setSelected(new Set());
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = (key) => {
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });
  };

  const importSelected = async (withAISummary) => {
    if (!selected.size) return;
    try {
      const payload = {
        userId: user.id,
        keys: Array.from(selected),
        ai_summary: !!withAISummary,
      };
      const res = await AdminUsersAPI.importFromUserS3(payload);
      onImportDone?.({
        ok: true,
        importedCount: res?.importedCount ?? payload.keys.length,
        ai: !!withAISummary,
      });
      onClose();
    } catch (e) {
      console.error("Import from user S3 failed", e);
      onImportDone?.({ ok: false, error: e.message || "Import failed" });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Review uploads from {user?.name || user?.email}</DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <FolderIcon fontSize="small" />
          <Typography variant="body2" color="text.secondary">
            Files shown are in the user's "User Folder". If not imported,
            they will remain here for up to 30 days before auto-deletion.
          </Typography>
        </Stack>

        {loading && <Typography>Loading…</Typography>}

        {!loading && items.length === 0 && (
          <Typography color="text.secondary">No uploads found.</Typography>
        )}

        {!loading && items.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={48} />
                <TableCell>Key</TableCell>
                <TableCell align="right" width={120}>
                  Size
                </TableCell>
                <TableCell width={220}>Uploaded</TableCell>
                <TableCell width={140}>Age (days)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((it) => (
                <TableRow
                  key={it.key}
                  hover
                  onClick={() => toggle(it.key)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell
                    padding="checkbox"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={selected.has(it.key)}
                      onChange={() => toggle(it.key)}
                    />
                  </TableCell>
                  <TableCell>{it.key}</TableCell>
                  <TableCell align="right">
                    {typeof it.size === "number"
                      ? `${(it.size / 1024 / 1024).toFixed(2)} MB`
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {it.uploaded_at ? fmtDate(it.uploaded_at) : "—"}
                  </TableCell>
                  <TableCell>
                    {typeof it.age_days === "number" ? it.age_days : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="outlined"
          startIcon={<CloudUploadIcon />}
          disabled={selected.size === 0}
          onClick={() => setAiPromptOpen(true)}
        >
          Import to Drive…
        </Button>
      </DialogActions>

      <Dialog
        open={aiPromptOpen}
        onClose={() => setAiPromptOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Generate AI summaries?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Do you want to start AI summaries for the imported file(s) after
            moving them into Drive?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAiPromptOpen(false);
              importSelected(false);
            }}
          >
            No
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setAiPromptOpen(false);
              importSelected(true);
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

/* ---------------------------
        Main Component
----------------------------*/
const UserManagementPane = () => {
  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);

  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rpp, setRpp] = useState(10);
  const [labourPackages, setLabourPackages] = useState([]);
  const [savingUserId, setSavingUserId] = useState(null);

  const [inviteEmail, setInviteEmail] = useState("");
  const [activeSection, setActiveSection] = useState("users"); 
  const [editingValidity, setEditingValidity] = useState({});

  // "users" | "products"

  const [addOpen, setAddOpen] = useState(false);
  const [submittingAdd, setSubmittingAdd] = useState(false);
  const [addForm, setAddForm] = useState({
    name: "",
    email: "",
    role: "member",
    sendInvite: true,
    tempPassword: "",
    labour_package: "none",
  });
  const setAF = (k, v) => setAddForm((s) => ({ ...s, [k]: v }));

  const [uploadsOpen, setUploadsOpen] = useState(false);
  const [uploadsUser, setUploadsUser] = useState(null);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await AdminUsersAPI.list({
        page: page + 1,
        pageSize: rpp,
        q: q || undefined,
        role: roleFilter || undefined,
        status: statusFilter || undefined,
      });
      setRows(res?.data || []);
      setTotal(res?.total ?? 0);
    } catch (e) {
      console.error("Failed to load users", e);
      setSnack({
        open: true,
        msg: e.message || "Failed to load users.",
        sev: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    AdminUsersAPI.listLabourPackages()
      .then((res) => setLabourPackages(res.data || []))
      .catch(() =>
        setSnack({
          open: true,
          msg: "Failed to load labour packages",
          sev: "error",
        })
      );
  }, []);

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rpp, roleFilter, statusFilter]);

  const refresh = () => fetchUsers();

  const filtered = useMemo(() => {
    if (!q) return rows;
    const term = q.toLowerCase();
    return rows.filter(
      (r) =>
        r?.email?.toLowerCase().includes(term) ||
        r?.name?.toLowerCase().includes(term)
    );
  }, [rows, q]);
  useEffect(() => {
    AdminUsersAPI.listLabourPackages()
      .then((res) => {
        if (res?.data?.ok) {
          setLabourPackages(res.data.data);
        }
      })
      .catch(() => {
        console.error("Failed to load labour packages");
      });
  }, []);

  const updateRole = async (id, role) => {
    try {
      await AdminUsersAPI.updateRole({ id, role });
      setSnack({ open: true, msg: "Role updated.", sev: "success" });
      refresh();
    } catch (e) {
      setSnack({
        open: true,
        msg: e.message || "Failed to update role.",
        sev: "error",
      });
    }
  };

  const toggleStatus = async (id, next) => {
    try {
      await AdminUsersAPI.toggleStatus({ id, status: next });
      setSnack({ open: true, msg: `User ${next}.`, sev: "success" });
      refresh();
    } catch (e) {
      setSnack({
        open: true,
        msg: e.message || "Failed to change status.",
        sev: "error",
      });
    }
  };

  const resendInvite = async (id) => {
    try {
      await AdminUsersAPI.resendInvite({ id });
      setSnack({ open: true, msg: "Invite sent.", sev: "success" });
    } catch (e) {
      setSnack({
        open: true,
        msg: e.message || "Failed to send invite.",
        sev: "error",
      });
    }
  };

  const resetPassword = async (id) => {
    try {
      await AdminUsersAPI.resetPassword({ id });
      setSnack({
        open: true,
        msg: "Password reset mail sent.",
        sev: "success",
      });
    } catch (e) {
      setSnack({
        open: true,
        msg: e.message || "Failed to reset password.",
        sev: "error",
      });
    }
  };

  const removeUser = async (id) => {
    console.log("🟢 DELETE BUTTON HANDLER -> removeUser called with id:", id);

    let shouldProceed = true;
    try {
      if (typeof window !== "undefined" && typeof window.confirm === "function") {
        shouldProceed = window.confirm(
          "Are you sure you want to delete this user? This action cannot be undone."
        );
      }
    } catch (err) {
      console.warn("⚠️ window.confirm not supported, continuing without prompt:", err);
    }

    if (!shouldProceed) {
      console.log("🟡 User cancelled deletion");
      return;
    }

    try {
      console.log("🟡 Calling AdminUsersAPI.deleteUser with id:", id);
      await AdminUsersAPI.deleteUser({ id });
      console.log("🟢 Delete API completed successfully");

      setRows((prev) => prev.filter((u) => u.id !== id));

      setSnack({
        open: true,
        msg: "User deleted successfully.",
        sev: "success",
      });
    } catch (e) {
      console.error("🔴 Delete user failed:", e);
      setSnack({
        open: true,
        msg:
          e.message ||
          "Failed to delete user. Please check console/network tab for details.",
        sev: "error",
      });
    }
  };

  const toggleCanUpload = async (id, can_upload) => {
    try {
      await AdminUsersAPI.updateCanUpload({ id, can_upload });
      setSnack({
        open: true,
        msg: can_upload ? "Uploads enabled." : "Uploads disabled.",
        sev: "success",
      });
      setRows((old) =>
        old.map((u) => (u.id === id ? { ...u, can_upload } : u))
      );
    } catch (e) {
      setSnack({
        open: true,
        msg: e.message || "Failed to update permission.",
        sev: "error",
      });
    }
  };

  const openUploads = (user) => {
    setUploadsUser(user);
    setUploadsOpen(true);
  };

  const onImportDone = ({ ok, importedCount, ai, error }) => {
    if (!ok) {
      setSnack({
        open: true,
        msg: error || "Import failed.",
        sev: "error",
      });
      return;
    }
    setSnack({
      open: true,
      msg: `Imported ${importedCount} file(s)${
        ai ? " with AI summaries" : ""
      }.`,
      sev: "success",
    });
  };

  const invite = async () => {
    if (!inviteEmail) return;
    try {
      await AdminUsersAPI.invite({ email: inviteEmail });
      setSnack({ open: true, msg: "Invite sent.", sev: "success" });
      setInviteEmail("");
      refresh();
    } catch (e) {
      setSnack({
        open: true,
        msg: e.message || "Failed to invite user.",
        sev: "error",
      });
    }
  };

  const createUser = async () => {
    if (!addForm.name?.trim()) {
      return setSnack({
        open: true,
        msg: "Name is required.",
        sev: "warning",
      });
    }
    if (!addForm.email?.trim()) {
      return setSnack({
        open: true,
        msg: "Email is required.",
        sev: "warning",
      });
    }
    if (!addForm.sendInvite && !addForm.tempPassword?.trim()) {
      return setSnack({
        open: true,
        msg: "Provide a temporary password or choose Send Invite.",
        sev: "warning",
      });
    }

    const payload = {
      name: addForm.name.trim(),
      email: addForm.email.trim(),
      role: addForm.role,
      status: addForm.sendInvite ? "invited" : "active",
      sendInvite: addForm.sendInvite,
      tempPassword: addForm.sendInvite ? undefined : addForm.tempPassword,
      licenses: {labour: addForm.labour_package || null},
      can_upload: false,
    };

    try {
      setSubmittingAdd(true);
      await AdminUsersAPI.create(payload);
      setSnack({ open: true, msg: "User created.", sev: "success" });
      setAddOpen(false);
      setAddForm({
        name: "",
        email: "",
        role: "member",
        sendInvite: true,
        tempPassword: "",
        labour_package: "none",
      });
      refresh();
    } catch (e) {
      setSnack({
        open: true,
        msg: e.message || "Failed to create user.",
        sev: "error",
      });
    } finally {
      setSubmittingAdd(false);
    }
  };

  // Simple global error logging
  useEffect(() => {
    const handleError = (error) => {
      console.error("🔴 Global error caught:", error);
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleError);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleError);
    };
  }, []);
  const ProductManagementSection = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editRow, setEditRow] = useState(null);

    const loadProducts = async () => {
      setLoading(true);
      try {
        const res = await AdminUsersAPI.listLabourPackages();
        setProducts(res.data || []);
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      loadProducts();
    }, []);

    const saveProduct = async () => {
      if (!editRow) return;

      const payload = {
        product_code: editRow.product_code,
        product_name: editRow.product_name,
        monthly_price_rs: editRow.monthly_price_rs,
        quarterly_price_rs: editRow.quarterly_price_rs,
        yearly_price_rs: editRow.yearly_price_rs,
        is_active: editRow.is_active,
      };

      try {
        if (editRow.id) {
          // 🔹 UPDATE
          await AdminUsersAPI.updateProduct(editRow.id, payload);
        } else {
          // 🔹 CREATE
          await AdminUsersAPI.createProduct(payload);
        }

        setEditRow(null);
        loadProducts();
      } catch (err) {
        console.error("Save product failed", err);
      }
    };

    return (
      <Paper sx={{ p: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Typography variant="h6">Labour Packages</Typography>
          <Button
            variant="contained"
            onClick={() =>
              setEditRow({
                product_code: "",
                product_name: "",
                monthly_price_rs: "",
                quarterly_price_rs: "",
                yearly_price_rs: "",
                is_active: true,
              })
            }
          >
            Add Package
          </Button>
        </Stack>

        {loading && <LinearProgress />}

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell align="right">Monthly ₹</TableCell>
              <TableCell align="right">Quarterly ₹</TableCell>
              <TableCell align="right">Yearly ₹</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {products.map((p) => (
              <TableRow key={p.id}>
                <TableCell>{p.product_code}</TableCell>
                <TableCell>{p.product_name}</TableCell>
                <TableCell align="right">{p.monthly_price_rs}</TableCell>
                <TableCell align="right">{p.quarterly_price_rs}</TableCell>
                <TableCell align="right">{p.yearly_price_rs}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={p.is_active ? "Active" : "Inactive"}
                    color={p.is_active ? "success" : "default"}
                  />
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    onClick={() => setEditRow({ ...p })}
                  >
                    Edit
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* EDIT / CREATE DIALOG */}
        <Dialog
          open={!!editRow}
          onClose={() => setEditRow(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {editRow?.id ? "Edit Package" : "Create Package"}
          </DialogTitle>

          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Product Code"
                  fullWidth
                  disabled={!!editRow?.id}
                  value={editRow?.product_code || ""}
                  onChange={(e) =>
                    setEditRow({ ...editRow, product_code: e.target.value })
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Product Name"
                  fullWidth
                  value={editRow?.product_name || ""}
                  onChange={(e) =>
                    setEditRow({ ...editRow, product_name: e.target.value })
                  }
                />
              </Grid>

              <Grid item xs={4}>
                <TextField
                  label="Monthly ₹"
                  type="number"
                  fullWidth
                  value={editRow?.monthly_price_rs || ""}
                  onChange={(e) =>
                    setEditRow({ ...editRow, monthly_price_rs: e.target.value })
                  }
                />
              </Grid>

              <Grid item xs={4}>
                <TextField
                  label="Quarterly ₹"
                  type="number"
                  fullWidth
                  value={editRow?.quarterly_price_rs || ""}
                  onChange={(e) =>
                    setEditRow({
                      ...editRow,
                      quarterly_price_rs: e.target.value,
                    })
                  }
                />
              </Grid>

              <Grid item xs={4}>
                <TextField
                  label="Yearly ₹"
                  type="number"
                  fullWidth
                  value={editRow?.yearly_price_rs || ""}
                  onChange={(e) =>
                    setEditRow({ ...editRow, yearly_price_rs: e.target.value })
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!editRow?.is_active}
                      onChange={(e) =>
                        setEditRow({
                          ...editRow,
                          is_active: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setEditRow(null)}>Cancel</Button>
            <Button variant="contained" onClick={saveProduct}>
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    );
  };
  
  

  return (
    <Box sx={{ display: "flex", height: "100%" }}>
      {/* ================= LEFT SIDE MENU ================= */}
      <Box
        sx={{
          width: 220,
          borderRight: "1px solid #e0e0e0",
          bgcolor: "#fafafa",
        }}
      >
        <Typography sx={{ p: 2 }} variant="subtitle1" fontWeight={600}>
          Admin
        </Typography>

        <Divider />

        <List dense>
          <ListItemButton
            selected={activeSection === "users"}
            onClick={() => setActiveSection("users")}
          >
            <ListItemText primary="Users" />
          </ListItemButton>

          <ListItemButton
            selected={activeSection === "products"}
            onClick={() => setActiveSection("products")}
          >
            <ListItemText primary="Product Management" />
          </ListItemButton>
        </List>
      </Box>

      {/* ================= RIGHT CONTENT ================= */}
      <Box sx={{ flex: 1, p: 2 }}>
        {activeSection === "users" && (
          <>
            {/* ⬇️⬇️⬇️ YOUR EXISTING USER MANAGEMENT CODE ⬇️⬇️⬇️ */}
            <Box sx={{ p: 2 }}>
              {/* Top filters + actions */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={2}
                >
                  <Typography variant="h6">User Management</Typography>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      size="small"
                      label="Search name or email"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                    />
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel id="role-filter">Role</InputLabel>
                      <Select
                        labelId="role-filter"
                        label="Role"
                        value={roleFilter}
                        onChange={(e) => {
                          setRoleFilter(e.target.value);
                          setPage(0);
                        }}
                      >
                        <MenuItem value="">All</MenuItem>
                        {ROLE_OPTIONS.map((r) => (
                          <MenuItem key={r} value={r}>
                            {r}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel id="status-filter">Status</InputLabel>
                      <Select
                        labelId="status-filter"
                        label="Status"
                        value={statusFilter}
                        onChange={(e) => {
                          setStatusFilter(e.target.value);
                          setPage(0);
                        }}
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="active">active</MenuItem>
                        <MenuItem value="invited">invited</MenuItem>
                        <MenuItem value="suspended">suspended</MenuItem>
                      </Select>
                    </FormControl>
                    <Tooltip title="Refresh">
                      <IconButton onClick={refresh}>
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                    <Button
                      variant="contained"
                      startIcon={<PersonAddAltIcon />}
                      onClick={() => setAddOpen(true)}
                    >
                      Add User
                    </Button>
                  </Stack>
                </Stack>

                {/* <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                  <TextField
                    size="small"
                    label="Invite user by email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    sx={{ width: 300 }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<PersonAddAltIcon />}
                    onClick={invite}
                  >
                    Invite
                  </Button>
                </Stack> */}
              </Paper>

              {/* Table */}
              <Paper sx={{ p: 2 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell width="18%">Name</TableCell>
                        <TableCell width="20%">Email</TableCell>
                        <TableCell width="12%">Role</TableCell>
                        <TableCell width="10%">Status</TableCell>
                        <TableCell width="14%">Labour Package</TableCell>
                        <TableCell width="12%">Valid From</TableCell>
                        <TableCell width="12%">Valid To</TableCell>
                        <TableCell width="14%">Uploads Allowed</TableCell>
                        <TableCell align="right" width="12%">
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {loading && (
                        <TableRow>
                          <TableCell colSpan={9} align="center">
                            Loading…
                          </TableCell>
                        </TableRow>
                      )}

                      {!loading && filtered.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={9} align="center">
                            No users found.
                          </TableCell>
                        </TableRow>
                      )}

                      {!loading &&
                        filtered.map((row) => {
                          const chipColor = STATUS_COLORS[row.status] || "default";
                          const nextStatus =
                            row.status === "suspended" ? "active" : "suspended";

                          const vf =
                            editingValidity[row.id]?.valid_from ??
                            row.valid_from ??
                            "";

                          const vt =
                            editingValidity[row.id]?.valid_to ??
                            row.valid_to ??
                            "";

                          return (
                            <TableRow key={row.id} hover>
                              {/* Name */}
                              <TableCell>{row.name || "-"}</TableCell>

                              {/* Email */}
                              <TableCell>{row.email || "-"}</TableCell>

                              {/* Role */}
                              <TableCell>
                                <Select
                                  size="small"
                                  value={row.role || "member"}
                                  onChange={(e) => updateRole(row.id, e.target.value)}
                                  sx={{ minWidth: 120 }}
                                >
                                  {ROLE_OPTIONS.map((r) => (
                                    <MenuItem key={r} value={r}>
                                      {r}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </TableCell>

                              {/* Status */}
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={row.status}
                                  color={chipColor}
                                />
                              </TableCell>

                              {/* Labour Package */}
                              <TableCell>
                                <Select
                                  size="small"
                                  fullWidth
                                  value={row?.labour_package?.id || ""}
                                  displayEmpty
                                  onChange={async (e) => {
                                    const productId = e.target.value;
                                    setSavingUserId(row.id);
                                    try {
                                      await AdminUsersAPI.updateUserLabourPackage({
                                        user_id: row.id,
                                        product_id: productId,
                                      });
                                    } finally {
                                      setSavingUserId(null);
                                      fetchUsers();
                                    }
                                  }}
                                >
                                  <MenuItem value="">
                                    <em>None</em>
                                  </MenuItem>
                                  {labourPackages.map((pkg) => (
                                    <MenuItem key={pkg.id} value={pkg.id}>
                                      {pkg.product_name}
                                    </MenuItem>
                                  ))}
                                </Select>

                                {savingUserId === row.id && (
                                  <LinearProgress sx={{ mt: 0.5 }} />
                                )}
                              </TableCell>

                              {/* Valid From */}
                              <TableCell>
                                <TextField
                                  type="date"
                                  size="small"
                                  value={row.valid_from || ""}
                                  onChange={async (e) => {
                                    const newValue = e.target.value;

                                    await AdminUsersAPI.updateUserValidity({
                                      user_id: row.id,
                                      valid_from: newValue,
                                      valid_to: row.valid_to,
                                    });

                                    fetchUsers();
                                  }}
                                />
                              </TableCell>

                              <TableCell>
                                <TextField
                                  type="date"
                                  size="small"
                                  value={row.valid_to || ""}
                                  onChange={async (e) => {
                                    const newValue = e.target.value;

                                    await AdminUsersAPI.updateUserValidity({
                                      user_id: row.id,
                                      valid_from: row.valid_from,
                                      valid_to: newValue,
                                    });

                                    fetchUsers();
                                  }}
                                />
                              </TableCell>

                              {/* Uploads Allowed */}
                              <TableCell>
                                <Switch
                                  size="small"
                                  checked={!!row.can_upload}
                                  onChange={(e) =>
                                    toggleCanUpload(row.id, e.target.checked)
                                  }
                                />
                              </TableCell>

                              {/* Actions */}
                              <TableCell align="right">
                                <Stack
                                  direction="row"
                                  spacing={0.5}
                                  justifyContent="flex-end"
                                  alignItems="center"
                                >
                                  {row.status === "invited" && (
                                    <Tooltip title="Resend invite">
                                      <IconButton
                                        size="small"
                                        onClick={() => resendInvite(row.id)}
                                      >
                                        <SendIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}

                                  <Tooltip title={nextStatus === "active" ? "Activate" : "Suspend"}>
                                    <IconButton
                                      size="small"
                                      onClick={() => toggleStatus(row.id, nextStatus)}
                                    >
                                      {row.status === "suspended" ? (
                                        <PlayCircleIcon fontSize="small" />
                                      ) : (
                                        <PauseCircleIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </Tooltip>

                                  <Tooltip title="Reset password">
                                    <IconButton
                                      size="small"
                                      onClick={() => resetPassword(row.id)}
                                    >
                                      <LockResetIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>

                                  <Tooltip title="Delete">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => removeUser(row.id)}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>

                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={total}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  rowsPerPage={rpp}
                  onRowsPerPageChange={(e) => {
                    setRpp(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                />
              </Paper>

              {/* Add User dialog */}
              <Dialog
                open={addOpen}
                onClose={() => setAddOpen(false)}
                fullWidth
                maxWidth="sm"
              >
                <DialogTitle>Add User</DialogTitle>
                <DialogContent dividers>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Full Name"
                        value={addForm.name}
                        onChange={(e) => setAF("name", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={addForm.email}
                        onChange={(e) => setAF("email", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Role</InputLabel>
                        <Select
                          label="Role"
                          value={addForm.role}
                          onChange={(e) => setAF("role", e.target.value)}
                        >
                          {ROLE_OPTIONS.map((r) => (
                            <MenuItem key={r} value={r}>
                              {r}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormGroup row sx={{ gap: 3 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={addForm.sendInvite}
                              onChange={(e) => setAF("sendInvite", e.target.checked)}
                            />
                          }
                          label="Send invite email"
                        />
                        {!addForm.sendInvite && (
                          <TextField
                            label="Temporary password"
                            type="text"
                            size="small"
                            value={addForm.tempPassword}
                            onChange={(e) =>
                              setAF("tempPassword", e.target.value)
                            }
                          />
                        )}
                      </FormGroup>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ mt: 0.5, mb: 0.5 }}>
                        Labour Law Access
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: 240 }}>
                        <InputLabel>Access</InputLabel>
                        <Select
                          label="Access"
                          value={addForm.labour_package}
                          onChange={(e) => setAF("labour_package", e.target.value)}
                        >
                          <MenuItem value="">None</MenuItem>
                          {labourPackages.map(pkg => (
                            <MenuItem key={pkg.product_code} value={pkg.product_code}>
                              {pkg.product_name}
                            </MenuItem>
                          ))}
                        </Select>

                      </FormControl>
                    </Grid>
                  </Grid>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setAddOpen(false)} disabled={submittingAdd}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={createUser}
                    disabled={submittingAdd}
                  >
                    {submittingAdd ? "Saving…" : "Create"}
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Review/Import dialog */}
              <UserUploadsDialog
                open={uploadsOpen}
                onClose={() => setUploadsOpen(false)}
                user={uploadsUser}
                onImportDone={onImportDone}
              />

              <Snackbar
                open={snack.open}
                autoHideDuration={2500}
                onClose={() => setSnack({ ...snack, open: false })}
              >
                <Alert
                  onClose={() => setSnack({ ...snack, open: false })}
                  severity={snack.sev}
                  sx={{ width: "100%" }}
                >
                  {snack.msg}
                </Alert>
              </Snackbar>
            </Box>
          </>
        )}

        {activeSection === "products" && (
          <>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Product Management
            </Typography>

            {/* Product management UI goes here */}
            <ProductManagementSection />
          </>
        )}
      </Box>
    </Box>
  );
};

export default UserManagementPane;
